/**
 * GrowthCandidateCard — iter100 · Carte candidat spécifique Growth
 *
 * **Brief CEO §12** : réutiliser le langage visuel de Prérec (typo, cartes,
 * couleurs, boutons) mais adapter le CONTENU au scoring Growth :
 *   - Score 84/100 avec badge ⭐/🟢/🟡/🔴
 *   - Synthèse IA (4-5 lignes)
 *   - Top 3 forces + preuves
 *   - Points à vérifier
 *   - Détail 8 sub-scores dépliable
 */
import { motion } from "framer-motion";
import { AlertCircle, ChevronDown, ChevronUp, FileText, PhoneCall, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import CallButton from "../call/CallButton";
import CallRecordingsInline from "../call/CallRecordingsInline";
import { formatDate, initials } from "../../lib/format";

const CATEGORY_META = {
    top:       { emoji: "⭐", label: "TOP PROFIL",         tone: "bg-yellow-50 text-yellow-800 border-yellow-300",  ring: "text-yellow-500" },
    promising: { emoji: "🟢", label: "TRÈS PROMETTEUR",    tone: "bg-emerald-50 text-emerald-800 border-emerald-300", ring: "text-emerald-500" },
    potential: { emoji: "🟡", label: "POTENTIEL À VÉRIFIER", tone: "bg-amber-50 text-amber-800 border-amber-300",      ring: "text-amber-500" },
    low:       { emoji: "🔴", label: "NON PRIORITAIRE",    tone: "bg-slate-100 text-slate-600 border-slate-300",     ring: "text-slate-400" },
};

const SUB_SCORE_LABELS = {
    proactivite:     { label: "Proactivité",     cap: 25 },
    academique:      { label: "Académique",      cap: 15 },
    analyse:         { label: "Analyse",         cap: 15 },
    business_growth: { label: "Business/Growth", cap: 15 },
    ia_digital:      { label: "IA / Digital",    cap: 10 },
    redaction:       { label: "Rédaction",       cap: 10 },
    leadership:      { label: "Leadership",      cap: 5  },
    curiosite:       { label: "Curiosité",       cap: 5  },
};


export default function GrowthCandidateCard({ candidate, index }) {
    const [expanded, setExpanded] = useState(false);
    const c = candidate;
    const score = c.growth_score;
    const category = c.growth_category || "low";
    const meta = CATEGORY_META[category] || CATEGORY_META.low;
    const notScored = score === null || score === undefined;
    // iter100.1 · CV vide → badge dédié plutôt que "low 0/100"
    // (9 candidats sans texte CV extractible côté Layan, pas 9 profils faibles).
    // Le flag `growth_cv_empty` est posé par le backend au moment du scoring.
    const emptyCv = c.growth_cv_empty === true;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(index * 0.02, 0.3), duration: 0.25 }}
            data-testid={`growth-candidate-card-${c.id}`}
            className={`rounded-2xl bg-white border-2 transition-all p-5 shadow-sm hover:shadow-md ${
                emptyCv ? "border-slate-300 border-dashed" : "border-slate-100 hover:border-shariti-orange/40"
            }`}
        >
            {/* HEADER — Rank + Nom + Score */}
            <div className="flex items-start gap-4">
                {/* Rank */}
                <div className="shrink-0 w-10 h-10 rounded-xl bg-shariti-cream flex items-center justify-center">
                    <span className="text-shariti-orange font-black text-lg">#{index + 1}</span>
                </div>

                {/* Identity */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Link
                            to={`/candidats/${c.id}`}
                            data-testid={`growth-candidate-link-${c.id}`}
                            className="text-xl font-extrabold tracking-tight text-shariti-black hover:text-shariti-orange transition block truncate"
                        >
                            {c.first_name} {c.last_name}
                        </Link>
                        {/* iter101 · CEO 2026-09-06 : accès direct au CV
                            depuis la carte, sans passer par la fiche.
                            `cv_url` est un CDN Layan public → ouvre en
                            nouvel onglet. */}
                        {c.cv_url && (
                            <a
                                href={c.cv_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`growth-card-cv-${c.id}`}
                                title="Ouvrir le CV dans un nouvel onglet"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-shariti-black text-white hover:bg-slate-700 transition"
                            >
                                <FileText className="h-3 w-3" />
                                CV
                            </a>
                        )}
                        {/* iter101 · Bouton Appel rapide (Twilio click-to-call)
                            directement sur la carte — évite le clic vers /prequal
                            quand la RH veut juste sonder rapidement. */}
                        {c.phone && (
                            <CallButton
                                candidate={c}
                                variant="wide"
                                className="!h-7 !px-2.5 !text-[10px] !gap-1"
                                dataTestid={`growth-card-call-${c.id}`}
                            />
                        )}
                        {/* iter101 · Score Avant Entretien visible dès qu'il est calculé */}
                        {c.growth_avant_entretien_score != null && (
                            <span
                                data-testid={`growth-avant-entretien-${c.id}`}
                                title="Score Avant Entretien (40% dossier + 60% préqualif)"
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-purple-300 bg-purple-50 text-purple-700"
                            >
                                Avant entretien · {c.growth_avant_entretien_score}/100
                            </span>
                        )}
                        {c.growth_prequal_score != null && (
                            <span
                                data-testid={`growth-prequal-badge-${c.id}`}
                                title="Score de préqualification téléphonique"
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-blue-300 bg-blue-50 text-blue-700"
                            >
                                Prequal · {c.growth_prequal_score}/100
                            </span>
                        )}
                    </div>
                    <div className="text-xs font-semibold text-slate-500 mt-0.5">
                        {c.city || "—"} · Postulé le {formatDate(c.applied_at || c.created_at)}
                        {c.email && <> · <span className="font-normal">{c.email}</span></>}
                    </div>
                </div>

                {/* Score */}
                <div className="shrink-0 flex flex-col items-center gap-1">
                    {emptyCv ? (
                        <div
                            data-testid={`growth-empty-cv-badge-${c.id}`}
                            className="w-16 h-16 rounded-full bg-slate-100 border-2 border-slate-300 border-dashed flex flex-col items-center justify-center text-slate-400"
                            title="Aucun texte CV extractible côté Layan — impossible de scorer."
                        >
                            <span className="text-[9px] font-black leading-tight text-center">CV<br/>VIDE</span>
                        </div>
                    ) : notScored ? (
                        <div className="w-16 h-16 rounded-full bg-slate-100 flex flex-col items-center justify-center border-2 border-slate-200">
                            <span className="text-xs font-black text-slate-400">À scorer</span>
                        </div>
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-white flex flex-col items-center justify-center border-4 border-current shadow-sm" style={{ borderColor: "currentColor" }}>
                            <span className={`text-2xl font-black leading-none ${meta.ring}`}>{score}</span>
                            <span className={`text-[9px] font-black leading-none ${meta.ring}`}>/100</span>
                        </div>
                    )}
                </div>
            </div>

            {/* CATEGORY BADGE */}
            {emptyCv ? (
                <div className="mt-3">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-black uppercase tracking-widest border-2 bg-slate-100 text-slate-600 border-slate-300 border-dashed">
                        📄 CV vide · analyse impossible
                    </span>
                    <p className="text-[11px] font-semibold text-slate-500 mt-1.5 leading-snug">
                        Aucun texte CV extractible côté Layan. Relance ce candidat pour récupérer un CV valide — ce N'EST PAS un profil faible.
                    </p>
                </div>
            ) : !notScored && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-black uppercase tracking-widest border-2 ${meta.tone}`}
                        data-testid={`growth-category-badge-${c.id}`}>
                        {meta.emoji} {meta.label}
                    </span>
                    {c.growth_confidence != null && (
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            Confiance IA · {c.growth_confidence}%
                        </span>
                    )}
                </div>
            )}

            {/* SUMMARY */}
            {c.growth_summary && (
                <div className="mt-3 text-sm text-slate-700 leading-relaxed" data-testid={`growth-summary-${c.id}`}>
                    <div className="flex items-start gap-2">
                        <Sparkles className="h-4 w-4 text-shariti-orange shrink-0 mt-0.5" />
                        <p className="italic">{c.growth_summary}</p>
                    </div>
                </div>
            )}

            {/* TOP FORCES + PREUVES */}
            {c.growth_top_forces?.length > 0 && (
                <div className="mt-3">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                        Top 3 forces
                    </div>
                    <ul className="space-y-1">
                        {c.growth_top_forces.map((f, i) => (
                            <li key={i} className="text-xs font-semibold text-slate-700 flex items-start gap-2">
                                <span className="text-emerald-500 font-black shrink-0">•</span>
                                <span>{f}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {c.growth_forces_preuves?.length > 0 && (
                <div className="mt-2 bg-emerald-50/60 border border-emerald-100 rounded-xl p-2.5">
                    <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1">
                        Preuves observées dans le dossier
                    </div>
                    <ul className="space-y-1">
                        {c.growth_forces_preuves.slice(0, 3).map((p, i) => (
                            <li key={i} className="text-[11px] text-emerald-900 font-medium leading-snug">
                                📄 {p}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* À VÉRIFIER */}
            {c.growth_a_verifier?.length > 0 && (
                <div className="mt-2 bg-amber-50/60 border border-amber-100 rounded-xl p-2.5">
                    <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Points à vérifier en préqualification
                    </div>
                    <ul className="space-y-1">
                        {c.growth_a_verifier.slice(0, 3).map((v, i) => (
                            <li key={i} className="text-[11px] text-amber-900 font-medium leading-snug">
                                ? {v}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* SUB-SCORES (expandable) */}
            {c.growth_sub_scores && (
                <div className="mt-3">
                    <button
                        onClick={() => setExpanded((v) => !v)}
                        data-testid={`growth-expand-${c.id}`}
                        className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-shariti-orange transition"
                    >
                        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        Détail des 8 critères
                    </button>
                    {expanded && (
                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-slate-100">
                            {Object.entries(SUB_SCORE_LABELS).map(([key, { label, cap }]) => {
                                const s = c.growth_sub_scores[key] || 0;
                                const pct = Math.round((s / cap) * 100);
                                const color = pct >= 70 ? "text-emerald-600" : pct >= 40 ? "text-amber-600" : "text-slate-400";
                                return (
                                    <div key={key} className="flex flex-col">
                                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                            {label}
                                        </div>
                                        <div className={`text-sm font-black ${color}`}>
                                            {s} <span className="text-slate-400 font-medium">/ {cap}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* PRIORITY QUESTIONS (personnalisées) */}
            {c.growth_priority_questions?.length > 0 && expanded && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-1.5">
                        Questions personnalisées à poser
                    </div>
                    <ul className="space-y-1.5">
                        {c.growth_priority_questions.map((q, i) => (
                            <li key={i} className="text-[11px] text-slate-700 font-medium italic leading-snug">
                                Q{i + 1} · « {q} »
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* iter101 · Enregistrements des appels directement sur la carte.
                Silencieux si aucun appel. */}
            {!emptyCv && <CallRecordingsInline candidateId={c.id} maxCalls={5} />}

            {/* iter101 · CTA Préqualifier — actif uniquement si pas encore préqualifié */}
            {!emptyCv && (
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        {c.growth_prequal_score != null
                            ? `Préqualifié le ${formatDate(c.growth_prequal_scored_at)} · ${c.growth_prequal_recommendation || "recommandation N/A"}`
                            : c.phone
                              ? "Prêt à appeler — Prérec fournira les 7 questions + 3 perso"
                              : "Aucun numéro de téléphone"}
                    </div>
                    <Link
                        to={`/growth/${c.id}/prequal`}
                        data-testid={`growth-prequal-btn-${c.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black bg-shariti-orange text-white hover:bg-shariti-orange-hover transition"
                    >
                        <PhoneCall className="h-3.5 w-3.5" />
                        {c.growth_prequal_score != null ? "Revoir la préqualif" : "Appeler & préqualifier"}
                    </Link>
                </div>
            )}
        </motion.div>
    );
}
